import { defineStore } from 'pinia';
import * as Sentry from '@sentry/vue';
import type { PromocodeErrorCode } from '@/types/promocode';
import { redeemPromocodeHandler } from '~/handlers/redeem-promocode.handler';

export type PromocodeNotificationType = 'success' | 'error' | 'warning';

export interface PromocodeNotification {
  is_open: boolean;
  type: PromocodeNotificationType;
  title: string;
  message: string;
}

export const usePromocodeStore = defineStore('promocode', () => {
  const authStore = useAuthStore();
  const { openLogin } = useAuth();
  const { userId, userEmail, userDocument, userHasDocument } = useUserData();

  const isRedeeming = ref(false);

  const notification = ref<PromocodeNotification>({
    is_open: false,
    type: 'success',
    title: '',
    message: '',
  });

  const NOTIFICATION_TITLES: Record<PromocodeNotificationType, string> = {
    success: 'Código resgatado com sucesso',
    error: 'Não foi possível resgatar',
    warning: 'Promocode já resgatado',
  };

  // Códigos que são estado esperado do usuário (não falha) — exibidos
  // como aviso (amarelo) em vez de erro (vermelho).
  const WARNING_ERROR_CODES: PromocodeErrorCode[] = ['already_redeemed'];

  function getNotificationType(
    error: string | null,
  ): Exclude<PromocodeNotificationType, 'success'> {
    return isKnownErrorCode(error) && WARNING_ERROR_CODES.includes(error)
      ? 'warning'
      : 'error';
  }

  function openNotification(type: PromocodeNotificationType, message: string) {
    notification.value = {
      is_open: true,
      type,
      title: NOTIFICATION_TITLES[type],
      message,
    };
  }

  function closeNotification() {
    notification.value.is_open = false;
  }

  const UNAVAILABLE_MESSAGE =
    'Serviço indisponível no momento. Tente novamente.';

  const ERROR_MESSAGES: Record<PromocodeErrorCode, string> = {
    not_found: 'Promocode inválido.',
    inactive: 'Este promocode não está mais disponível.',
    not_started: 'Este promocode ainda não está disponível.',
    expired: 'Este promocode expirou.',
    out_of_stock: 'Este promocode atingiu o limite de usos.',
    already_redeemed: 'Você já resgatou este promocode.',
    not_eligible: 'Sua conta não é elegível para este promocode.',
    bonus_failed: 'Não foi possível aplicar o bônus. Tente novamente.',
  };

  function isKnownErrorCode(error: string | null): error is PromocodeErrorCode {
    return (
      error !== null &&
      Object.prototype.hasOwnProperty.call(ERROR_MESSAGES, error)
    );
  }

  function getErrorMessage(
    status: number | null,
    error: string | null,
    message: string | null,
  ) {
    if (status === null) return UNAVAILABLE_MESSAGE;

    if (status === 429) {
      return 'Código esgotado';
    }

    // o core é a fonte de verdade do texto; o mapa local fica como fallback
    // pra quando a resposta não traz message (corpo ilegível, 200 fora do
    // contrato) ou pra um código novo ainda não mapeado.
    if (message?.trim()) return message.trim();

    if (isKnownErrorCode(error)) {
      return ERROR_MESSAGES[error];
    }

    return 'Promocode não aplicado.';
  }

  async function redeemPromocode(code: string): Promise<boolean> {
    // Enter no input ignora o :disabled do botão — sem este guard,
    // Enter repetido dispararia POSTs duplicados durante o resgate
    if (isRedeeming.value || !code.trim()) return false;

    if (!authStore.isAuthenticated) {
      openLogin();

      return false;
    }

    if (!userId.value || !userEmail.value) {
      // autenticado, mas o perfil ainda não carregou (GET /auth/user-profile)
      openNotification('error', UNAVAILABLE_MESSAGE);

      return false;
    }

    if (!userHasDocument.value) {
      openNotification(
        'error',
        'Complete seu cadastro (CPF) para resgatar promocodes.',
      );

      return false;
    }

    isRedeeming.value = true;

    try {
      const result = await redeemPromocodeHandler({
        code: code.trim().toUpperCase(),
        user_ext_id: String(userId.value),
        user_email: userEmail.value,
        user_cpf: userDocument.value,
      });

      if (result.ok) {
        openNotification('success', `Você recebeu: ${result.bonus.name}`);

        return true;
      }

      openNotification(
        getNotificationType(result.error),
        getErrorMessage(result.status, result.error, result.message),
      );

      const isExpectedBusinessError =
        result.status !== null &&
        result.status < 500 &&
        (result.status === 429 ||
          result.status === 422 ||
          isKnownErrorCode(result.error));

      if (!isExpectedBusinessError) {
        // erros de negócio mapeados são esperados e seriam ruído na triagem;
        // só rede/timeout, 5xx e códigos desconhecidos interessam ao Sentry
        Sentry.captureException(
          result.cause ??
            new Error(`promocode redeem failed (status ${result.status})`),
          {
            tags: { source: 'promocode.redeem' },
            extra: { status: result.status, errorCode: result.error },
          },
        );
      }

      return false;
    } finally {
      isRedeeming.value = false;
    }
  }

  return {
    isRedeeming,
    notification,
    redeemPromocode,
    openNotification,
    closeNotification,
  };
});
